import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  ArrowDownCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Plus,
  FileText
} from 'lucide-react';
import WithdrawalRequestModal from '../../components/investor/WithdrawalRequestModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawalRequestsPage() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [cashBalance, setCashBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchPortfolio();
  }, [token]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/investor/withdrawal-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.items);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolioValue(response.data.current_portfolio_value || 0);
      setCashBalance(response.data.bank_balance || 0);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      approved: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
      processed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      interest_distribution: 'Interest Distribution',
      partial_redemption: 'Partial Redemption',
      full_redemption: 'Full Redemption'
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#B8860B]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="withdrawal-requests-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Withdrawal Requests</h1>
          <p className="text-gray-400 mt-1">Manage your withdrawal and redemption requests</p>
        </div>
        <Button 
          onClick={() => setShowModal(true)}
          className="bg-[#B8860B] hover:bg-[#9A7209] text-white"
          data-testid="new-request-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Available Balance <span className="text-[10px] text-gray-500">(Total Portfolio)</span></p>
                <p className="text-xl font-bold text-[#B8860B]" data-testid="wd-total-portfolio">{formatCurrency(portfolioValue)}</p>
                <p className="text-[11px] text-gray-500 mt-1">
                  Cash on hand: <span className="text-gray-300" data-testid="wd-cash-available">{formatCurrency(cashBalance)}</span>
                  {portfolioValue > cashBalance && (
                    <span className="ml-1 text-amber-400">· requests above this trigger a trade-out</span>
                  )}
                </p>
              </div>
              <ArrowDownCircle className="h-8 w-8 text-[#B8860B]/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Pending Requests</p>
                <p className="text-xl font-bold text-yellow-400">
                  {requests.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Withdrawn</p>
                <p className="text-xl font-bold text-green-400">
                  {formatCurrency(
                    requests
                      .filter(r => r.status === 'processed')
                      .reduce((sum, r) => sum + (r.processed_amount || 0), 0)
                  )}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#B8860B]" />
            Request History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <ArrowDownCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No withdrawal requests yet</p>
              <p className="text-sm text-gray-500 mt-1">Click "New Request" to submit your first request</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div 
                  key={request.id} 
                  className="p-4 bg-[#2a2a2a] rounded-lg border border-[#3a3a3a]"
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{getTypeLabel(request.request_type)}</span>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-gray-400">
                        Submitted: {formatDate(request.created_at)}
                      </p>
                      {request.notes && (
                        <p className="text-sm text-gray-500 mt-2">"{request.notes}"</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#B8860B]">
                        {formatCurrency(request.processed_amount || request.requested_amount)}
                      </p>
                      {request.status === 'processed' && request.reference_number && (
                        <p className="text-xs text-gray-400">Ref: {request.reference_number}</p>
                      )}
                    </div>
                  </div>
                  {request.admin_notes && (
                    <div className="mt-3 p-2 bg-[#1a1a1a] rounded text-sm">
                      <span className="text-gray-400">Admin: </span>
                      <span className="text-gray-300">{request.admin_notes}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Request Modal */}
      <WithdrawalRequestModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        portfolioValue={portfolioValue}
        onSuccess={fetchRequests}
      />
    </div>
  );
}
